import React, { useState, useEffect, useCallback } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import api from '../../../services/api';

ChartJS.register(ArcElement, Tooltip, Legend);

const CATEGORIES = [
  { value: 'model-calculations', label: 'Model Calculation Assumptions' },
  { value: 'age-pension', label: 'Age Pension Parameters' },
  { value: 'deeming', label: 'Deeming Rates' },
  { value: 'annuity', label: 'Annuity Assessment Rules' },
  { value: 'sis-rates', label: 'SIS Minimum Drawdown Rates' },
  { value: 'portfolios', label: 'Portfolio Capital Market Assumptions' },
  { value: 'asset-classes', label: 'Asset Class Assumptions' },
  { value: 'lookthrough', label: 'Portfolio Compositions (Lookthrough)' },
  { value: 'correlations', label: 'Asset Class Correlations' },
];

// "As at" dates by category. Legislated parameters (age pension / deeming / SIS)
// and the model methodology carry the parameter review date; the CMA-derived
// panels (portfolio & asset-class assumptions, correlations, lookthrough) were
// reset at the 1 July 2026 SAA review, so they carry that date. Update these at
// each review.
const PARAMS_AS_AT = '20 September 2026';
const CMA_AS_AT = '1 July 2026';
const AS_AT_BY_CATEGORY = {
  'model-calculations': PARAMS_AS_AT,
  'age-pension': PARAMS_AS_AT,
  'deeming': PARAMS_AS_AT,
  'annuity': PARAMS_AS_AT,
  'sis-rates': PARAMS_AS_AT,
  'portfolios': CMA_AS_AT,
  'asset-classes': CMA_AS_AT,
  'correlations': CMA_AS_AT,
  'lookthrough': CMA_AS_AT,
};

// Ordered colour palette for the lookthrough pies/tables. Asset-class buckets
// are now driven live by the CMA taxonomy (broad_asset_class), so colours are
// assigned by position rather than by a fixed name map.
const LOOKTHROUGH_PALETTE = [
  '#011E41', '#F3BD48', '#5E819C', '#337B91', '#A6C6C1',
  '#874564', '#AA8090', '#127080', '#BBCBD7', '#6C757D', '#EFE6D5',
];

// Portfolio-level CMAs are shown only for the Drummond Strategic series (DS*)
// and the private market portfolios (DPM / DPC). Everything else (e.g. DDP,
// tailored client portfolios) is excluded from the portfolio assumption views.
const isStrategicOrPrivate = (code) =>
  !!code && (code.startsWith('DS') || code === 'DPM' || code === 'DPC');

const AssumptionsModal = ({ show, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState('model-calculations');
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Download the full assumptions content (all categories) as a PDF built
  // server-side from /api/retirement/assumptions-pdf.
  const handleExportPdf = async () => {
    setExporting(true);
    setError(null);
    try {
      const response = await api.get('/api/retirement/assumptions-pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Model_Assumptions.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const needsApiData = useCallback((cat) => {
    // All the data-driven CMA panels (portfolio summaries, asset-class
    // assumptions, correlations, and the asset-class lookthrough) render live
    // from the active CMA scenario via /api/retirement/portfolio-data.
    return ['portfolios', 'asset-classes', 'correlations', 'lookthrough'].includes(cat);
  }, []);

  useEffect(() => {
    if (!show) return;
    if (!needsApiData(selectedCategory)) return;
    if (portfolioData) return;

    setLoading(true);
    setError(null);
    api.get('/api/retirement/portfolio-data')
      .then((res) => {
        setPortfolioData(res.data);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load portfolio data');
      })
      .finally(() => setLoading(false));
  }, [show, selectedCategory, portfolioData, needsApiData]);

  if (!show) return null;

  const renderContent = () => {
    if (needsApiData(selectedCategory)) {
      if (loading) {
        return (
          <div className="text-center py-5">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Loading data...
          </div>
        );
      }
      if (error) {
        return (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-circle me-2"></i>
            Error loading data: {error}
          </div>
        );
      }
    }

    switch (selectedCategory) {
      case 'model-calculations': return <ModelCalculations />;
      case 'age-pension': return <AgePension />;
      case 'deeming': return <DeemingRates />;
      case 'annuity': return <AnnuityRules />;
      case 'sis-rates': return <SISRates />;
      case 'portfolios': return portfolioData ? <Portfolios data={portfolioData} /> : null;
      case 'asset-classes': return portfolioData ? <AssetClasses data={portfolioData} /> : null;
      case 'lookthrough': return portfolioData ? <Lookthrough data={portfolioData} /> : null;
      case 'correlations': return portfolioData ? <Correlations data={portfolioData} /> : null;
      default: return null;
    }
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-gear me-2"></i>Model Assumptions
            </h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <div className="row mb-4">
              <div className="col-md-8">
                <label htmlFor="assumptionCategory" className="form-label fw-bold">Select Category:</label>
                <select
                  className="form-select"
                  id="assumptionCategory"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>As at:</strong> {AS_AT_BY_CATEGORY[selectedCategory] || PARAMS_AS_AT}
            </div>

            <div id="assumptionContent">
              {renderContent()}
            </div>
          </div>
          <div className="modal-footer">
            {error && (
              <span className="text-danger small me-auto">
                <i className="bi bi-exclamation-circle me-1"></i>{error}
              </span>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleExportPdf}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Generating PDF...
                </>
              ) : (
                <>
                  <i className="bi bi-file-earmark-pdf me-2"></i>Export to PDF
                </>
              )}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   STATIC CATEGORY COMPONENTS
   ================================================================ */

const ModelCalculations = () => {
  const [openAccordion, setOpenAccordion] = useState(null);

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const accordionSections = [
    {
      id: 'modelbasis',
      title: 'Modelling Basis & Time Steps',
      content: (
        <>
          <p><strong>Annual Time Steps:</strong> The model operates on an annual basis. Each simulation step represents one full year, with investment returns, withdrawals, pension assessments, and all other cashflows applied once per year. No intra-year (monthly or quarterly) calculations are performed.</p>
          <p><strong>Integer Ages:</strong> Ages are assumed to be integer values. The model treats each person as turning the next age at the start of each annual step — for example, a person entered as age 67 is assumed to be exactly 67 at the start of the projection, turning 68 at the start of year two, and so on.</p>
          <p><strong>Financial Year Timing:</strong> The projection effectively assumes the current financial year starts today. No adjustment is made for the actual date within the financial year — all annual amounts (withdrawals, pension entitlements, income streams) are modelled as full-year figures from the outset.</p>
        </>
      ),
    },
    {
      id: 'inflation',
      title: 'Inflation & Dollar Values',
      content: (
        <>
          <p><strong>Inflation Rate:</strong> All measures of inflation are assumed to be price inflation at 2.5% p.a. (fixed, not adjustable). This single rate is applied uniformly to deflate nominal returns, index income streams, and express all values in real (today's dollar) terms. For simplicity, this inflation rate is held constant across all simulated scenarios regardless of whether investment returns are strong or weak in a given path. There is no consistent empirical literature establishing a reliable directional relationship between inflation and real investment returns over retirement-length horizons, and as such the use of a fixed inflation assumption is not expected to materially bias the modelled outcomes relative to a stochastic inflation approach.</p>
          <p><strong>Inflation and Income:</strong> Age pension (aiming to be conservative given current wage-based indexation method), all non-portfolio income streams, rental income, and annuity payments are grown by the inflation assumption.</p>
          <p><strong>Dollar Values:</strong> All inputs and outputs are in <strong>real (today's dollar)</strong> terms</p>
          <ul>
            <li>Portfolio growth: (After-tax return - 2.5%) × portfolio balance</li>
          </ul>
        </>
      ),
    },
    {
      id: 'withdrawal',
      title: 'Income Preference & Withdrawal Strategy',
      content: (
        <>
          <p>The Income Preference setting determines how much is withdrawn from the portfolio each year during retirement. All withdrawal strategies only apply from retirement age onwards — no portfolio withdrawals occur during the pre-retirement phase.</p>

          <p><strong>"How much can I get?" (Dynamic)</strong></p>
          <ul>
            <li>Calculates the maximum sustainable annual withdrawal using a PMT (annuity payment) function</li>
            <li>Each year, the withdrawal is recalculated based on the current portfolio balance, the number of remaining retirement years, the real after-tax return rate, and the desired bequest amount</li>
            <li>Formula: <em>PMT(real return, years remaining, current balance, desired bequest)</em></li>
            <li>The withdrawal amount adjusts each year as the portfolio balance and remaining time change — it is not a fixed amount</li>
            <li>Withdrawals are capped at the current portfolio balance (the portfolio cannot go negative)</li>
            <li>All values are in real (today's dollar) terms</li>
          </ul>

          <p><strong>"I want a fixed %" (Fixed Percentage)</strong></p>
          <ul>
            <li>Withdraws a constant percentage of the portfolio balance at the start of each year</li>
            <li>Formula: <em>Withdrawal = Portfolio Balance × Withdrawal Rate</em></li>
            <li>The dollar amount withdrawn changes each year as the portfolio balance fluctuates</li>
            <li>Option to use SIS Minimum Drawdown Rates instead, which automatically adjusts the percentage based on the member's age (see SIS Minimum Pension Standards section)</li>
          </ul>

          <p><strong>"I want a fixed inflation adjusted $ amount" (Fixed Dollar)</strong></p>
          <ul>
            <li>Targets a fixed annual income amount in real (today's dollar) terms</li>
            <li>The target income represents total desired household income — the portfolio only funds the gap after other income sources are accounted for</li>
            <li>Formula: <em>Portfolio Withdrawal = max(0, Target Income - Age Pension - Employment Income - Other Income - Rental Income - Annuity Income)</em></li>
            <li>As age pension and other income increase, the amount drawn from the portfolio decreases</li>
            <li>Withdrawals are capped at the current portfolio balance</li>
            <li><strong>Step-down withdrawals:</strong> Optionally allows different target income amounts for different age ranges (e.g. $75,000 p.a. from age 67 to 80, then $60,000 p.a. from age 80 onwards)</li>
          </ul>

          <p className="mb-0"><strong>Year-by-year balance calculation:</strong> For all strategies, the end-of-year portfolio balance is: <em>Balance = max(0, Previous Balance × (1 + Return) - Withdrawal)</em>, where the return is the stochastic real after-tax return for that year and simulation path.</p>
        </>
      ),
    },
    {
      id: 'tax',
      title: 'Tax Treatment',
      content: (
        <>
          <p><strong>Blended Tax Rate Input:</strong> Should reflect the <strong>retirement tax rate</strong> on investment returns</p>
          <p><strong>Default:</strong> 0.0% (assumes tax-free pension phase)</p>
          <p><strong>Application:</strong> Applied to portfolio returns only. After-tax return = Gross return × (1 - Blended tax rate)</p>
          <p><strong>Income Sources:</strong></p>
          <ul>
            <li><strong>Employment income:</strong> Enter as after-tax amount</li>
            <li><strong>Other income:</strong> Enter as net of tax amount</li>
            <li><strong>Rental income:</strong> Enter as net of tax and expenses</li>
            <li><strong>Age Pension:</strong> Modelled as tax-free</li>
            <li><strong>Portfolio income:</strong> Taxed at blended rate</li>
          </ul>
          <p className="mb-0 text-muted small"><em>Note: Tax treatment is simplified. Does not distinguish between capital gains, franking credits, or tax-free components.</em></p>
        </>
      ),
    },
    {
      id: 'fees',
      title: 'Fees & Costs',
      content: (
        <>
          <p><strong>Investment Fees:</strong> Returns modelled as net of model manager and underlying fund fees, but not including advice, platform or administration fees</p>
          <p><strong>Fee Indexation:</strong> Fees assumed to be static (based on % of assets)</p>
        </>
      ),
    },
    {
      id: 'assets',
      title: 'Asset Treatment & Consumption',
      content: (
        <>
          <h6>Assets Consumed (Drawn Down):</h6>
          <ul>
            <li><strong>Financial Assets:</strong> Yes - primary source of retirement income</li>
          </ul>
          <h6>Assets NOT Consumed (Fixed Value):</h6>
          <ul>
            <li><strong>Non-Financial Assets:</strong> Held at constant real value throughout projection. Included in age pension asset test only</li>
            <li><strong>Investment Property:</strong> Held at constant real value during rental period (selected by user), then assumed sold (value drops to zero). Add sale proceeds as lump sum contribution if reinvesting</li>
          </ul>
          <h6>Default Values:</h6>
          <ul>
            <li><strong>Financial Assets:</strong> $1,000,000 (editable), not prescriptive, <em>reflecting a typical starting point for retirement savings</em></li>
            <li><strong>Non-Financial Assets:</strong> $80,000 (editable), not prescriptive, <em>reflecting a typical starting point for non-financial assets</em></li>
            <li>All other inputs default to $0 to ensure adviser customisation</li>
          </ul>
          <p className="mb-0 text-muted small"><em>Note: The model takes a portfolio approach - does not distinguish between superannuation and non-superannuation assets, or allocate per person in couple scenarios.</em></p>
        </>
      ),
    },
    {
      id: 'portfolio',
      title: 'Portfolio Approach & Super/Non-Super',
      content: (
        <>
          <p><strong>Household Approach:</strong> Financial assets are treated as a single household pool</p>
          <p><strong>No Distinction Between:</strong></p>
          <ul>
            <li>Superannuation vs non-superannuation investments</li>
            <li>Person 1 vs Person 2 assets (in couple scenarios)</li>
            <li>Account types or structures</li>
          </ul>
          <p><strong>Implications:</strong></p>
          <ul>
            <li>Use blended tax rate that reflects overall household tax position</li>
            <li>SIS minimum drawdown calculated on total financial assets</li>
            <li>Age pension calculated on combined household position</li>
          </ul>
        </>
      ),
    },
    {
      id: 'couple',
      title: 'Couple Scenario Assumptions',
      content: (
        <>
          <p><strong>Age Inputs:</strong></p>
          <ul>
            <li>Age 1: Primary person (typically eldest)</li>
            <li>Age 2: Partner</li>
            <li>Retirement age applies to Age 1 (eldest partner)</li>
          </ul>
          <p><strong>Pre-Retirement Phase:</strong></p>
          <ul>
            <li>Annual contributions apply to household until eldest reaches retirement age</li>
            <li>Contributions stop when eldest person retires</li>
          </ul>
          <p><strong>Age Pension:</strong></p>
          <ul>
            <li>Full couple pension if both above age 67</li>
            <li>Half couple pension if only one above age 67</li>
            <li>Uses couple thresholds for income and asset tests if in a couple</li>
          </ul>
          <p><strong>SIS Minimum Drawdown:</strong></p>
          <ul>
            <li>Applies SIS rate based on eldest partner</li>
          </ul>
          <p className="mb-0"><strong>Investment Mix:</strong> Both persons assumed invested in same portfolio allocation</p>
        </>
      ),
    },
    {
      id: 'income',
      title: 'Income Sources & Modelling',
      content: (
        <>
          <p><strong>Employment Income:</strong></p>
          <ul>
            <li>Entered as an after-tax amount</li>
            <li>During the pre-retirement phase, employment income is assumed to be earned and spent — it does not impact portfolio outcomes</li>
            <li>From retirement age, employment income contributes towards achieving the desired annual income, reducing the amount drawn from the portfolio</li>
            <li>Continues for the specified number of years from retirement age</li>
            <li>Assessable for age pension income test from age 67 (Work Bonus of $7,800 p.a. applied automatically)</li>
          </ul>
          <p><strong>Other Income:</strong></p>
          <ul>
            <li>Applied during retirement for specified years</li>
            <li>Examples: business income, trust distributions</li>
            <li>Fully assessable for age pension</li>
          </ul>
          <p><strong>Rental Income:</strong></p>
          <ul>
            <li>Net rental income (after expenses and tax)</li>
            <li>Investment property value and rental income assumed to grow with inflation during held period</li>
            <li>Property assumed sold when rental income ends</li>
            <li>Both income and property value assessable for age pension</li>
          </ul>
          <p><strong>Excess Income Treatment:</strong></p>
          <ul>
            <li>All income consumed (not reinvested)</li>
            <li>Withdrawals calculated to meet target based on selected strategy</li>
            <li>Portfolio income adjusts automatically based on balance and strategy</li>
          </ul>
        </>
      ),
    },
    {
      id: 'contributions',
      title: 'Superannuation Contributions',
      content: (
        <>
          <p><strong>When Applied:</strong> Pre-retirement years only (before eldest partner reaches retirement age)</p>
          <p><strong>Amount:</strong> Fixed real annual amount (real dollars)</p>
          <p><strong>Growth:</strong> Contributions added to portfolio balance</p>
          <p><strong>Timing:</strong> Added at start of each year before returns applied</p>
          <p><strong>Cessation:</strong> Automatically stops at retirement age</p>
          <p className="mb-0 text-muted small"><em>Note: Contribution caps and limits not enforced. Adviser should ensure inputs comply with current rules.</em></p>
        </>
      ),
    },
    {
      id: 'annuity',
      title: 'Lifetime Annuity Income',
      content: (
        <>
          <p><strong>Age Pension Treatment:</strong></p>
          <ul>
            <li><strong>Before Commencement (Age &le; 84):</strong> 60% of purchase price (asset test, deflated by CPI), 0% income test</li>
            <li className="mt-2"><strong>After Commencement (Age &le; 84):</strong> 60% of purchase price (asset test, deflated by CPI), 60% of payment (income)</li>
            <li><strong>After Commencement (Age &gt; 84):</strong> 30% of purchase price (asset test, deflated by CPI), 60% of payment (income)</li>
          </ul>
          <p><strong>Asset Test Note:</strong> The annuity asset test uses the nominal purchase price deflated by CPI (2.5% p.a.) because the model runs in real terms.</p>
          <p><strong>Inflation-Adjusted Annuity:</strong></p>
          <ul>
            <li>Payment starts at specified commencement age</li>
            <li>Fixed real annual payment amount (CPI linked)</li>
            <li>Continues for entire projection period (lifetime)</li>
          </ul>
          <p><strong>Market-Linked Annuity:</strong></p>
          <ul>
            <li>For deferred annuities, payments are indexed by the reference portfolio return from purchase. Actual payments commence at the specified commencement age at the indexed-up amount</li>
            <li>Payments fluctuate based on reference portfolio returns (less inflation at 2.5%)</li>
            <li>Reference portfolios: Cash, Conservative, Conservative Balanced, Balanced, or Growth Index</li>
            <li>Tax is not applied to reference portfolio returns (annuity income is taxed separately at the recipient level)</li>
            <li><strong>Accelerated Payment Reduction:</strong> Optional rate (0-8%) that reduces payments faster in early years to provide higher initial income. Each year's payment is adjusted by: <em>Payment × (1 + Reference Return - Inflation - Reduction Rate)</em></li>
          </ul>
          <p><strong>Household Wealth Valuation:</strong> The annuity value shown in the household wealth chart is based on the maximum voluntary withdrawal value under the capital access schedule. This is modelled as a linear decrease from the purchase amount to $0 at age 86, expressed in real (inflation-adjusted) terms.</p>
          <p className="mb-0"><strong>Longevity:</strong> Model does not account for mortality — assumes annuity pays for full projection period. Model does not account for guaranteed death benefits or access to lump sum withdrawals.</p>
        </>
      ),
    },
    {
      id: 'pension',
      title: 'Age Pension Calculation',
      content: (
        <>
          <p><strong>Eligibility:</strong> Age 67 for both singles and couples</p>
          <p><strong>Calculation Method:</strong> More restrictive of income test and asset test determines final pension</p>
          <p><strong>Income Test:</strong> Work bonus applied to employment income only, then income free area applied to total assessable income (employment + other + deemed + rental + annuity). Reduction at 50c per dollar over the threshold.</p>
          <p><strong>Couple &mdash; One Eligible:</strong> When only one partner is age pension eligible, the full couple pension is reduced by both income and asset tests first, then the result is halved. This matches Services Australia&rsquo;s calculation order.</p>
          <p><strong>Reassessment:</strong> Calculated every year based on:</p>
          <ul>
            <li>Current portfolio balance (financial assets), adjusted for lump sum transactions in that period</li>
            <li>Non-financial assets (real constant)</li>
            <li>Investment property value (if held)</li>
            <li>Employment, other, and rental income for that year</li>
            <li>Annuity assessable amounts (if applicable)</li>
          </ul>
          <p><strong>Indexation Assumptions:</strong></p>
          <ul>
            <li>The model operates in real (today's) dollars. All age pension rates, asset test thresholds, income test thresholds, and deeming thresholds are held constant in real terms throughout the projection. This implicitly assumes they are indexed in line with inflation (CPI at 2.5% p.a.)</li>
            <li>In practice, pension rates and thresholds may be indexed by different measures (e.g. CPI, PBLCI, MTAWE) which may diverge from CPI over time. This simplification may modestly overstate or understate future age pension entitlements</li>
            <li>Deeming rates are assumed to remain at current legislated rates for the duration of the projection</li>
          </ul>
          <p><strong>Limitations:</strong></p>
          <ul>
            <li>Uses current legislated rates and thresholds (assumed to be indexed with CPI in real terms)</li>
            <li>Does not model future policy changes to rates, thresholds, or eligibility criteria</li>
            <li>No other social security concessions, such as Rent Assistance or other supplementary payments, are modelled — only the Age Pension is considered</li>
          </ul>
        </>
      ),
    },
    {
      id: 'timeframe',
      title: 'Modelling Timeframe & Phases',
      content: (
        <>
          <p><strong>Pre-Retirement Phase:</strong> From current age to retirement age</p>
          <ul>
            <li><strong>Duration:</strong> Retirement age - Current age (Age 1)</li>
            <li><strong>Portfolio growth:</strong> Stochastic returns applied (Monte Carlo)</li>
            <li><strong>Contributions:</strong> Annual contributions added</li>
            <li><strong>Withdrawals:</strong> None</li>
            <li><strong>Income sources:</strong> Employment, other, and rental income are assumed to be earned and spent during this phase — they do not affect portfolio outcomes</li>
            <li><strong>Age pension:</strong> Based on age, not retirement age</li>
          </ul>
          <p><strong>Retirement Phase:</strong> From retirement age onwards</p>
          <ul>
            <li><strong>Duration:</strong> "Years in Retirement" input (defaults to the youngest person's age to 100 to account for longevity risk; can be overridden by the adviser)</li>
            <li><strong>Portfolio growth:</strong> Stochastic returns applied</li>
            <li><strong>Withdrawals:</strong> Strategy-based (Fixed, Dynamic, or SIS Minimum)</li>
            <li><strong>Income sources:</strong> Employment, other, rental, and annuity income contribute towards achieving the desired annual income, reducing portfolio withdrawals</li>
            <li><strong>Age pension:</strong> Calculated annually from age 67</li>
          </ul>
          <p><strong>Total Projection Period:</strong> Pre-retirement years + Retirement years</p>
          <p className="mb-0"><strong>Monte Carlo Simulation:</strong> 10,000 scenarios run for full projection period to generate outcome distribution</p>
        </>
      ),
    },
    {
      id: 'sis',
      title: 'SIS Minimum Pension Standards',
      content: (
        <>
          <p><strong>Application:</strong> Only when "Use SIS Minimum Drawdown" option is selected under the "I want a fixed %" income preference</p>
          <p><strong>Calculation:</strong></p>
          <ul>
            <li>Applied as a percentage of the <strong>financial assets (portfolio balance) only</strong> at the start of each year — non-financial assets, investment property, and annuity values are not included in the SIS minimum calculation</li>
            <li>Rate based on age (4% under 65, up to 14% for 95+)</li>
            <li>Automatically adjusts as age increases each year</li>
          </ul>
          <p><strong>Couple Treatment:</strong></p>
          <ul>
            <li>Financial assets assumed split 50/50 between members</li>
            <li>Each person's age-based SIS rate is applied to their half of the financial assets</li>
            <li>Total minimum withdrawal = Sum of both individual minimums</li>
          </ul>
          <p><strong>Excess Income:</strong> If income from other sources exceeds SIS minimum, excess is consumed (not reinvested)</p>
          <p className="mb-0"><strong>Limitation:</strong> Does not enforce maximum pension limits or transfer balance caps</p>
        </>
      ),
    },
    {
      id: 'longevity',
      title: 'Longevity Risk & Years in Retirement',
      content: (
        <>
          <p>The "Years in Retirement" input determines the projection horizon. It defaults to the number of years for the youngest person to reach age 100, accounting for longevity risk. This default updates automatically when age inputs change and can be overridden by the adviser. Selecting too few years risks understating the funding required for retirement.</p>

          <p><strong>Life Expectancy Reference (Australian Government Actuary Life Tables 2020-22, with 25-year improvement factors):</strong></p>
          <div className="table-responsive">
            <table className="table table-sm table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Current Age</th>
                  <th>Male Life Expectancy</th>
                  <th>Female Life Expectancy</th>
                  <th>Male 25% Survival Age</th>
                  <th>Female 25% Survival Age</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>55</td><td>87</td><td>90</td><td>93</td><td>95</td></tr>
                <tr><td>60</td><td>88</td><td>90</td><td>93</td><td>95</td></tr>
                <tr><td>65</td><td>88</td><td>90</td><td>93</td><td>95</td></tr>
                <tr><td>67</td><td>88</td><td>90</td><td>93</td><td>96</td></tr>
                <tr><td>70</td><td>89</td><td>91</td><td>94</td><td>96</td></tr>
                <tr><td>75</td><td>90</td><td>92</td><td>95</td><td>97</td></tr>
                <tr><td>80</td><td>91</td><td>93</td><td>95</td><td>97</td></tr>
                <tr><td>85</td><td>93</td><td>94</td><td>96</td><td>98</td></tr>
                <tr><td>90</td><td>95</td><td>96</td><td>98</td><td>99</td></tr>
              </tbody>
            </table>
          </div>

          <p><strong>Key Concepts:</strong></p>
          <ul>
            <li><strong>Life expectancy</strong> is the age by which 50% of the population will have passed away — half will live longer</li>
            <li><strong>25% survival age</strong> is the age at which 25% of the population are still alive — a more prudent planning horizon</li>
            <li>For <strong>couples</strong>, the relevant measure is the last survivor life expectancy (the age at which the last member of the couple passes away), which is materially longer than individual life expectancy. For a couple both aged 67, the last survivor life expectancy is approximately 94, with a 25% chance the last survivor lives beyond 97</li>
          </ul>

          <p><strong>Considerations for High Net Worth Clients:</strong></p>
          <ul>
            <li>Research consistently shows that individuals with higher wealth have materially longer life expectancies than the general population</li>
            <li>The reference data above is based on population-wide statistics and may understate life expectancy for high net worth retirees</li>
            <li>Advisers should consider using the 25% survival age or planning to age 100 for the youngest member as a more prudent planning horizon</li>
          </ul>

          <p><strong>Model Treatment:</strong> The model does not apply mortality — all simulation paths project for the full specified number of years regardless of age. The projection horizon is entirely determined by the adviser's input.</p>
          <p className="mb-0"><strong>Survival Assumption:</strong> All persons are assumed to survive the full projection period. For couples, the model does not account for first death — outcomes on first death (e.g. reversion to single pension rates, changes to asset test thresholds, loss of a member's income) could be materially different to those illustrated. The model also does not account for disablement or other changes in circumstances that may affect retirement outcomes.</p>
        </>
      ),
    },
    {
      id: 'householdwealth',
      title: 'Household Wealth Over Time Chart',
      content: (
        <>
          <p>The Household Wealth Over Time chart shows the total estimated value of all household assets across the projection period. It is a stacked area chart comprising the following components:</p>
          <ul>
            <li><strong>Portfolio Balance (50th percentile):</strong> The median simulated value of the household's financial assets (super, pension accounts, investments) after accounting for investment returns and drawdowns.</li>
            <li><strong>Investment Property:</strong> The value of any investment property held. The input value is assumed to grow in line with inflation (2.5% p.a.), so it remains constant in real (today's dollar) terms across the projection.</li>
            <li><strong>Annuity Value:</strong> The estimated maximum voluntary withdrawal value under the capital access schedule. This is modelled as a linear decrease from the purchase amount to $0 at age 86 in nominal terms, then deflated by inflation to express in real terms. This component only appears if an annuity has been entered.</li>
            <li><strong>Non-Financial Assets:</strong> The value of other non-financial assets (e.g. contents, vehicles). Like investment property, this is assumed to grow with inflation and therefore remains constant in real terms across the projection.</li>
          </ul>
          <p className="mb-0"><strong>Note:</strong> All values are expressed in real (today's dollar) terms. The total household wealth at any point is the sum of all four components. Because investment property and non-financial assets are assumed to maintain their real value, changes in total household wealth are primarily driven by the portfolio balance and annuity value.</p>
        </>
      ),
    },
  ];

  return (
    <>
      <h4 className="mb-4"><i className="bi bi-calculator me-2"></i>Model Calculation Assumptions</h4>

      <div className="alert alert-info mb-4">
        <h5 className="alert-heading"><i className="bi bi-bullseye me-2"></i>Purpose of the Model</h5>
        <p className="mb-2">This retirement income model is designed to help retirees and pre-retirees, together with their financial adviser, understand the range of possible financial outcomes in retirement. By simulating thousands of potential scenarios using Monte Carlo methods, the model aims to turn uncertainty into actionable insight — helping answer questions such as:</p>
        <ul className="mb-2">
          <li>What are the chances of meeting my income goals throughout retirement?</li>
          <li>How might market downturns early in retirement affect my long-term plan?</li>
          <li>Can I afford to spend more — or give more — with confidence?</li>
        </ul>
        <p className="mb-0">The model is intended to support informed decision-making around retirement risks including longevity risk (the possibility of outliving savings) and sequencing risk (the impact of negative returns early in retirement when drawing down). It is not a forecast or guarantee of future outcomes, but a planning tool that illustrates how a retirement strategy might perform across a wide range of market conditions. Results should be interpreted with the guidance of a qualified financial adviser.</p>
      </div>

      <p className="lead">Key assumptions used in the retirement income calculator, including both user-configurable defaults and fixed parameters. All values in the calculator except for Objectives Based Portfolio return are in real (post inflation) terms. Inflation is assumed to be 2.5% - the mid point of the RBA's target band. This cannot be adjusted because it would invalidate the capital market assumptions embedded in the model.</p>

      <h5 className="mb-3"><i className="bi bi-person-gear me-2 text-primary"></i>User-Configurable Defaults (Can Be Overridden)</h5>
      <div className="table-responsive mb-4">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>Parameter</th>
              <th>Default Value</th>
              <th>Description</th>
              <th>Where to Change</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Blended Tax Rate</strong></td>
              <td>0.0%</td>
              <td>Tax rate applied to investment returns. Consider marginal tax rate and mix of income types (pension / accumulation for super, capital gains, dividends, interest)</td>
              <td>Step 3: Personal Details</td>
            </tr>
            <tr>
              <td><strong>Years in Retirement</strong></td>
              <td>30 years</td>
              <td>Default projection period. The average life expectancy calculated based on Australian Government Actuary Life Tables 2020-22 with 25-year improvement factors for a 67-year-old male is 87.7, and for a female is 89.8, and a for a 67-year-old couple their life expectancy (the age at which the last person will pass away on average) is 94.</td>
              <td>Step 5: Retirement Goals</td>
            </tr>
            <tr>
              <td><strong>Desired Bequest</strong></td>
              <td>$0</td>
              <td>Real dollar value of portfolio intended to remain at end of projection period</td>
              <td>Step 5: Retirement Goals</td>
            </tr>
            <tr>
              <td><strong>Drawdown Method</strong></td>
              <td>Dynamic</td>
              <td>Default withdrawal strategy. Options: Fixed Dollar, Fixed Percentage, Dynamic, or SIS Minimum</td>
              <td>Step 5: Retirement Goals</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h5 className="mb-3"><i className="bi bi-lock me-2 text-danger"></i>Fixed Parameters (Cannot Be Changed)</h5>
      <div className="table-responsive mb-4">
        <table className="table table-bordered table-hover">
          <thead className="table-dark">
            <tr>
              <th>Parameter</th>
              <th>Value</th>
              <th>Description</th>
              <th>Usage in Model</th>
            </tr>
          </thead>
          <tbody>
            <tr className="table-warning">
              <td><strong>Inflation / CPI</strong></td>
              <td>2.5% p.a.</td>
              <td>Assumed long-term inflation rate, mid point of RBA's target band</td>
              <td>Used throughout entire calculator to keep all values in post inflation terms</td>
            </tr>
            <tr>
              <td><strong>Number of Simulations</strong></td>
              <td>10,000</td>
              <td>Monte Carlo simulation runs</td>
              <td>Generates probability distribution of outcomes (10th, 25th, 50th, 75th, 90th percentiles)</td>
            </tr>
            <tr>
              <td><strong>Investment returns</strong></td>
              <td>After-tax return (based on Drummond capital market assumptions for portfolios and asset classes) less inflation assumption</td>
              <td>Real growth rate before and after retirement</td>
              <td>Applied to portfolio balance during accumulation and decumulation phase</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="alert alert-warning">
        <i className="bi bi-exclamation-triangle me-2"></i>
        <strong>Important:</strong> All dollar amounts in inputs and outputs are in real (today's dollar) terms unless otherwise specified. The model accounts for inflation internally in growth calculations and dynamic withdrawals.
      </div>

      <h5 className="mb-3 mt-4"><i className="bi bi-gear me-2 text-info"></i>How Key Elements Are Modelled</h5>

      <div className="accordion" id="modellingAccordion">
        {accordionSections.map((section) => (
          <div className="accordion-item" key={section.id}>
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${openAccordion === section.id ? '' : 'collapsed'}`}
                type="button"
                onClick={() => toggleAccordion(section.id)}
              >
                {section.title}
              </button>
            </h2>
            <div className={`accordion-collapse collapse ${openAccordion === section.id ? 'show' : ''}`}>
              <div className="accordion-body">
                {section.content}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const AgePension = () => {
  const singleAnnual = (1237.70 * 26).toLocaleString('en-AU', { minimumFractionDigits: 2 });
  const coupleAnnual = (1866.00 * 26).toLocaleString('en-AU', { minimumFractionDigits: 2 });

  return (
    <>
      <h4 className="mb-4"><i className="bi bi-cash-stack me-2"></i>Age Pension Parameters</h4>

      <div className="row mb-4">
        <div className="col-md-6 mb-3">
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="card-title"><i className="bi bi-people me-2 text-primary"></i>Maximum Pension Amounts (Annual)</h6>
              <div className="d-flex justify-content-between mb-2">
                <span>Single:</span>
                <strong>${singleAnnual}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Couple:</span>
                <strong>${coupleAnnual}</strong>
              </div>
              <small className="text-muted">Includes pension plus supplements</small>
            </div>
          </div>
        </div>
        <div className="col-md-6 mb-3">
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="card-title"><i className="bi bi-calendar me-2 text-primary"></i>Pension Age Threshold</h6>
              <div className="text-center">
                <h2 className="text-primary mb-0">67</h2>
                <small className="text-muted">Age at which pension eligibility starts</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h5 className="mb-3">Income Test Parameters</h5>
      <div className="table-responsive mb-4">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>Parameter</th>
              <th>Single</th>
              <th>Couple</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Work Income Threshold</td>
              <td>${(226 * 26).toLocaleString('en-AU')}</td>
              <td>${(396 * 26).toLocaleString('en-AU')}</td>
              <td>Income threshold before pension reduction applies</td>
            </tr>
            <tr>
              <td>Work Bonus</td>
              <td colSpan="2">${(300 * 26).toLocaleString('en-AU')}</td>
              <td>Additional exemption for employment income</td>
            </tr>
            <tr>
              <td>Income Taper Rate</td>
              <td colSpan="2">50%</td>
              <td>Pension reduction rate for income above threshold</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h5 className="mb-3">Asset Test Thresholds</h5>
      <div className="table-responsive mb-4">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>Home Ownership</th>
              <th>Single</th>
              <th>Couple</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Homeowner</td>
              <td>${(333000).toLocaleString('en-AU')}</td>
              <td>${(499000).toLocaleString('en-AU')}</td>
              <td>Asset threshold before pension reduction</td>
            </tr>
            <tr>
              <td>Non-Homeowner</td>
              <td>${(600000).toLocaleString('en-AU')}</td>
              <td>${(766000).toLocaleString('en-AU')}</td>
              <td>Asset threshold before pension reduction</td>
            </tr>
            <tr>
              <td>Asset Taper Rate</td>
              <td colSpan="2">$3.00 per fortnight per $1,000 of assets</td>
              <td>Pension reduction rate for assets above threshold</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h5 className="mb-3">Couple — One Partner Eligible</h5>
      <div className="alert alert-warning mb-4">
        <i className="bi bi-people me-2"></i>
        <strong>When only one member of a couple has reached age pension age (67):</strong>
        <ul className="mb-0 mt-2">
          <li>The <strong>couple</strong> income and asset test thresholds are used (not single thresholds)</li>
          <li>The full couple pension rate is reduced by income and asset test reductions as normal</li>
          <li>The resulting pension amount is then <strong>halved</strong>, reflecting that only one partner is eligible to receive payment</li>
          <li>Once both partners reach age 67, the full couple pension amount applies (no halving)</li>
        </ul>
      </div>

      <div className="alert alert-secondary">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Note:</strong> The age pension calculation uses the more restrictive of the income test and asset test to determine the final pension amount.
      </div>
    </>
  );
};

const DeemingRates = () => (
  <>
    <h4 className="mb-4"><i className="bi bi-percent me-2"></i>Deeming Rates</h4>

    <p className="lead">Deeming rates are used to calculate deemed income from financial assets for Age Pension means testing.</p>

    <div className="row mb-4">
      <div className="col-md-6 mb-3">
        <div className="card border-primary">
          <div className="card-body">
            <h6 className="card-title"><i className="bi bi-arrow-down me-2 text-success"></i>Lower Deeming Rate</h6>
            <h2 className="text-primary mb-3">1.75% p.a.</h2>
            <p className="mb-0"><strong>Applies to assets below threshold:</strong></p>
            <div className="d-flex justify-content-between mt-2">
              <span>Single:</span>
              <strong>${(66800).toLocaleString('en-AU')}</strong>
            </div>
            <div className="d-flex justify-content-between">
              <span>Couple:</span>
              <strong>${(110600).toLocaleString('en-AU')}</strong>
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-6 mb-3">
        <div className="card border-warning">
          <div className="card-body">
            <h6 className="card-title"><i className="bi bi-arrow-up me-2 text-warning"></i>Upper Deeming Rate</h6>
            <h2 className="text-warning mb-3">3.75% p.a.</h2>
            <p className="mb-0"><strong>Applies to assets above threshold:</strong></p>
            <div className="d-flex justify-content-between mt-2">
              <span>Single:</span>
              <strong>${(66800).toLocaleString('en-AU')}</strong>
            </div>
            <div className="d-flex justify-content-between">
              <span>Couple:</span>
              <strong>${(110600).toLocaleString('en-AU')}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <h5 className="mb-3">How Deeming Works</h5>
    <div className="card bg-light mb-4">
      <div className="card-body">
        <p className="mb-2"><strong>Example for a Single Person with $100,000 in financial assets:</strong></p>
        <ul className="mb-0">
          <li>First $66,800 deemed at 1.75% = ${(66800 * 0.0175).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</li>
          <li>Remaining ${(100000 - 66800).toLocaleString('en-AU')} deemed at 3.75% = ${((100000 - 66800) * 0.0375).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</li>
          <li><strong>Total deemed income: ${(66800 * 0.0175 + (100000 - 66800) * 0.0375).toLocaleString('en-AU', { minimumFractionDigits: 2 })} per year</strong></li>
        </ul>
      </div>
    </div>

    <div className="alert alert-info">
      <i className="bi bi-info-circle me-2"></i>
      <strong>Note:</strong> Deemed income is used in the income test regardless of the actual income earned on those assets.
    </div>
  </>
);

const AnnuityRules = () => (
  <>
    <h4 className="mb-4"><i className="bi bi-file-earmark-text me-2"></i>Annuity Assessment Rules for Age Pension</h4>

    <p className="lead">Lifetime annuities are assessed for Age Pension purposes based on these rules: https://guides.dss.gov.au/social-security-guide/4/9/3/35 https://guides.dss.gov.au/social-security-guide/1/1/a/280.</p>

    <h5 className="mb-3">Assessment Rates</h5>
    <div className="table-responsive mb-4">
      <table className="table table-bordered table-hover">
        <thead className="table-dark">
          <tr>
            <th>Period</th>
            <th>Asset Test</th>
            <th>Income Test</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Before Commencement (Age ≤ 84)</td>
            <td>60% of purchase price</td>
            <td>0%</td>
            <td>Asset test applies, no income test until payments start</td>
          </tr>
          <tr>
            <td>Before Commencement (Age &gt; 84)</td>
            <td>30% of purchase price</td>
            <td>0%</td>
            <td>Reduced asset test after age 84, no income test</td>
          </tr>
          <tr>
            <td>After Commencement (Age ≤ 84)</td>
            <td>60% of purchase price</td>
            <td>60% of annual payment</td>
            <td>Standard assessment rates</td>
          </tr>
          <tr className="table-success">
            <td>After Commencement (Age &gt; 84)</td>
            <td>30% of purchase price</td>
            <td>60% of annual payment</td>
            <td>Reduced asset test after age 84</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="row">
      <div className="col-md-6 mb-3">
        <div className="card bg-light">
          <div className="card-body">
            <h6 className="card-title"><i className="bi bi-cake2 me-2 text-primary"></i>Age Threshold</h6>
            <div className="text-center">
              <h2 className="text-primary mb-0">84</h2>
              <small className="text-muted">Asset test rate reduces after this age</small>
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-6 mb-3">
        <div className="card bg-light">
          <div className="card-body">
            <h6 className="card-title"><i className="bi bi-percent me-2 text-primary"></i>Income Test Rate</h6>
            <div className="text-center">
              <h2 className="text-primary mb-0">60%</h2>
              <small className="text-muted">Of annual payment after commencement</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="alert alert-info mt-4">
      <i className="bi bi-info-circle me-2"></i>
      <strong>Note:</strong> The asset test applies from the purchase date. The income test only applies once annuity payments commence at the specified commencement age.
    </div>
  </>
);

const SISRates = () => {
  const rates = [
    { range: 'Under 65', rate: '4.0%', example: (500000 * 0.04).toLocaleString('en-AU') },
    { range: '65-74', rate: '5.0%', example: (500000 * 0.05).toLocaleString('en-AU') },
    { range: '75-79', rate: '6.0%', example: (500000 * 0.06).toLocaleString('en-AU') },
    { range: '80-84', rate: '7.0%', example: (500000 * 0.07).toLocaleString('en-AU') },
    { range: '85-89', rate: '9.0%', example: (500000 * 0.09).toLocaleString('en-AU') },
    { range: '90-94', rate: '11.0%', example: (500000 * 0.11).toLocaleString('en-AU') },
    { range: '95+', rate: '14.0%', example: (500000 * 0.14).toLocaleString('en-AU') },
  ];

  return (
    <>
      <h4 className="mb-4"><i className="bi bi-graph-up me-2"></i>SIS Minimum Drawdown Rates</h4>

      <p className="lead">The Superannuation Industry (Supervision) regulations specify minimum annual drawdown rates based on age for account-based pensions.</p>

      <div className="table-responsive mb-4">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>Age Range</th>
              <th>Minimum Rate</th>
              <th>Example: $500,000 Balance</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((r) => (
              <tr key={r.range}>
                <td>{r.range}</td>
                <td>{r.rate}</td>
                <td>${r.example}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card bg-light mb-4">
        <div className="card-body">
          <h6 className="card-title"><i className="bi bi-lightbulb me-2 text-warning"></i>How SIS Minimum Rates Work</h6>
          <ul className="mb-0">
            <li>Rates are applied as a percentage of the account balance at the start of each financial year</li>
            <li>Clients must withdraw at least the minimum amount, but they can withdraw more</li>
            <li>Minimum rates help ensure superannuation provides income throughout retirement</li>
            <li>If using SIS minimums in this calculator, rates automatically adjust as clients age</li>
          </ul>
        </div>
      </div>

      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Note:</strong> These are minimum rates. You can select "Use SIS Minimum Drawdown" in the calculator to have withdrawal rates automatically adjust based on age.
      </div>
    </>
  );
};

/* ================================================================
   DYNAMIC CATEGORY COMPONENTS (require API data)
   ================================================================ */

// Portfolio summaries, asset-class assumptions, and the correlation matrix are
// sourced live from the active CMA scenario (Investment Config) via
// /api/retirement/portfolio-data, and rendered here so the modal shows the
// actual current assumptions rather than pointing elsewhere.
const fmtPct = (value, dp = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return `${(value * 100).toFixed(dp)}%`;
};

// Small footnote reminding users where these figures are edited.
const CmaSourceNote = () => (
  <div className="alert alert-info">
    <i className="bi bi-info-circle me-2"></i>
    These assumptions are managed in <strong>Investment Config &rsaquo; Capital Market Assumptions</strong>.
    The calculator reads the active default scenario at run time, so any change made there flows straight through here.
  </div>
);

const Portfolios = ({ data }) => {
  const summary = Array.isArray(data?.portfolio_summary) ? data.portfolio_summary : [];
  const byCode = {};
  summary.forEach((row) => {
    const code = row.Portfolio;
    if (!code) return;
    if (!byCode[code]) byCode[code] = { code };
    if (row.Metric === 'Annual Return') byCode[code].ret = row.Value;
    else if (row.Metric === 'Annual Volatility') byCode[code].vol = row.Value;
  });
  const rows = Object.values(byCode)
    .filter((r) => isStrategicOrPrivate(r.code))
    .sort((a, b) => (b.ret ?? -Infinity) - (a.ret ?? -Infinity));

  return (
    <>
      <h4 className="mb-4"><i className="bi bi-pie-chart me-2"></i>Portfolio Capital Market Assumptions</h4>
      <p className="lead">Expected return and volatility for the Drummond Strategic series and private market portfolios, derived live from the active CMA scenario.</p>
      {rows.length === 0 ? (
        <div className="alert alert-secondary">No portfolio assumptions available for your access profile.</div>
      ) : (
        <div className="table-responsive mb-4">
          <table className="table table-striped table-hover">
            <thead className="table-dark">
              <tr>
                <th>Portfolio</th>
                <th className="text-end">Expected Return (p.a.)</th>
                <th className="text-end">Volatility (p.a.)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code}>
                  <td><strong>{r.code}</strong></td>
                  <td className="text-end">{fmtPct(r.ret)}</td>
                  <td className="text-end">{fmtPct(r.vol)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CmaSourceNote />
    </>
  );
};

const AssetClasses = ({ data }) => {
  const rows = Array.isArray(data?.ac_returns) ? data.ac_returns : [];
  const sorted = [...rows].sort((a, b) => (b.annual_return ?? -Infinity) - (a.annual_return ?? -Infinity));

  return (
    <>
      <h4 className="mb-4"><i className="bi bi-layers me-2"></i>Asset Class Assumptions</h4>
      <p className="lead">Expected return and volatility for the underlying sub-asset classes in the active CMA scenario.</p>
      {sorted.length === 0 ? (
        <div className="alert alert-secondary">No asset class assumptions available.</div>
      ) : (
        <div className="table-responsive mb-4">
          <table className="table table-striped table-hover">
            <thead className="table-dark">
              <tr>
                <th>Sub-Asset Class</th>
                <th className="text-end">Expected Return (p.a.)</th>
                <th className="text-end">Volatility (p.a.)</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.asset_class}>
                  <td>{r.asset_class}</td>
                  <td className="text-end">{fmtPct(r.annual_return)}</td>
                  <td className="text-end">{fmtPct(r.annual_volatility)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CmaSourceNote />
    </>
  );
};

const Lookthrough = ({ data }) => {
  const portfolios = Array.isArray(data?.portfolio_lookthrough) ? data.portfolio_lookthrough : [];

  return (
    <>
      <h4 className="mb-4"><i className="bi bi-diagram-3 me-2"></i>Portfolio Compositions (Asset Class Lookthrough)</h4>
      <p className="lead">Asset class allocations for the Drummond Strategic series and private market portfolios, aggregated live from the active CMA scenario's sub-asset-class weights.</p>

      {portfolios.length === 0 ? (
        <div className="alert alert-secondary">No portfolio compositions available for your access profile.</div>
      ) : (
        portfolios.map((p) => {
          const chartColors = p.rows.map((_, i) => LOOKTHROUGH_PALETTE[i % LOOKTHROUGH_PALETTE.length]);
          const pieData = {
            labels: p.rows.map((r) => r.asset_class),
            datasets: [{
              data: p.rows.map((r) => r.weight * 100),
              backgroundColor: chartColors,
              borderWidth: 2,
              borderColor: '#ffffff',
            }],
          };
          const pieOptions = {
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (context) => `${context.label}: ${context.parsed.toFixed(1)}%`,
                },
              },
            },
          };

          return (
            <div className="card mb-4" key={p.code}>
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">{p.code}</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-7">
                    <div className="table-responsive">
                      <table className="table table-sm table-striped mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th>Asset Class</th>
                            <th className="text-end">Allocation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.rows.map((row, i) => (
                            <tr key={row.asset_class}>
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: chartColors[i],
                                    width: '12px',
                                    height: '12px',
                                    display: 'inline-block',
                                    marginRight: '8px',
                                  }}
                                ></span>
                                {row.asset_class}
                              </td>
                              <td className="text-end"><strong>{fmtPct(row.weight)}</strong></td>
                            </tr>
                          ))}
                          <tr>
                            <td><strong>Total</strong></td>
                            <td className="text-end"><strong>{fmtPct(p.total)}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="col-md-5">
                    <Pie data={pieData} options={pieOptions} />
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}

      <CmaSourceNote />
    </>
  );
};

const Correlations = ({ data }) => {
  const cov = Array.isArray(data?.ac_covariance) ? data.ac_covariance : [];

  const sacSet = new Set();
  const corr = {};
  cov.forEach((c) => {
    if (!c.asset_class_1 || !c.asset_class_2) return;
    sacSet.add(c.asset_class_1);
    sacSet.add(c.asset_class_2);
    corr[`${c.asset_class_1}|||${c.asset_class_2}`] = c.correlation;
    corr[`${c.asset_class_2}|||${c.asset_class_1}`] = c.correlation;
  });
  const sacs = [...sacSet].sort();

  const getCorr = (a, b) => {
    if (a === b) return 1;
    const v = corr[`${a}|||${b}`];
    return v === undefined ? null : v;
  };

  // Correlation heat: near +1 red (moves together), near -1 green (diversifying).
  const cellStyle = (v) => {
    if (v === null || v === undefined || isNaN(v)) return {};
    if (v >= 0) return { backgroundColor: `rgba(220, 53, 69, ${(v * 0.55).toFixed(3)})` };
    return { backgroundColor: `rgba(145, 207, 155, ${(Math.abs(v) * 0.6).toFixed(3)})` };
  };

  return (
    <>
      <h4 className="mb-4"><i className="bi bi-grid-3x3 me-2"></i>Asset Class Correlations</h4>
      <p className="lead">Correlation matrix of the sub-asset classes from the active CMA scenario. Higher (redder) values move together; lower or negative (greener) values diversify.</p>
      {sacs.length === 0 ? (
        <div className="alert alert-secondary">No correlation data available.</div>
      ) : (
        <div className="table-responsive mb-4">
          <table className="table table-bordered table-sm mb-0" style={{ fontSize: '0.75rem' }}>
            <thead className="table-dark">
              <tr>
                <th></th>
                {sacs.map((s) => (
                  <th key={s} className="text-center" style={{ whiteSpace: 'nowrap' }}>{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sacs.map((rowSac) => (
                <tr key={rowSac}>
                  <th className="table-dark" style={{ whiteSpace: 'nowrap' }}>{rowSac}</th>
                  {sacs.map((colSac) => {
                    const v = getCorr(rowSac, colSac);
                    return (
                      <td key={colSac} className="text-center" style={cellStyle(v)}>
                        {v === null || v === undefined ? '-' : v.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CmaSourceNote />
    </>
  );
};

export default AssumptionsModal;
